import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useUpdateWisdomChat } from '@modules/oracle/hooks';
import { trackWisdomEvents } from '@modules/oracle/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import { Chat } from 'generated/graphql';
import { FormEvent, useState } from 'react';

interface WisdomChatRenameModalContentProps {
  chat: Omit<Chat, 'responses'>;
}

export const WisdomChatRenameModalContent = ({
  chat,
}: WisdomChatRenameModalContentProps) => {
  const { closeModal } = useMuiModalContext();
  const [chatTitle, setChatTitle] = useState(chat?.title || '');
  const { showSuccessNotification } = useNotificationContext();
  const [updateWisdomChat, { loading: isUpdatingWisdomChat }] =
    useUpdateWisdomChat({
      onCompleted: () => {
        showSuccessNotification({
          contentComponent: 'Wisdom chat name updated successfully!',
          props: {},
        });

        closeModal();
      },
    });

  const renameWisdomChat = async (e: FormEvent) => {
    e.preventDefault();
    updateWisdomChat({
      editToken: chat?.editToken ?? '',
      title: chatTitle.trim(),
    });

    trackWisdomEvents({
      eventName: 'Wisdom - Chat Name Updated',
      chatName: chat?.title,
      newChatName: chatTitle,
    });
  };

  return (
    <Box component="form" onSubmit={renameWisdomChat}>
      <ModalHeader title="Edit Chat Name" />
      <DialogContent dividers>
        <TextField
          fullWidth
          required
          label="Chat Name"
          variant="outlined"
          value={chatTitle}
          onChange={(e) => setChatTitle(e.target.value)}
          autoFocus
        />
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => closeModal()}
          endIcon={<EscHotKeyIcon />}
        >
          Cancel
        </Button>
        <LoadingButton
          autoFocus
          variant="contained"
          type="submit"
          disabled={!chatTitle.trim() || chatTitle === chat?.title}
          endIcon={<ReturnHotKeyIcon />}
          loading={isUpdatingWisdomChat}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};
